/**
 * Judge.me Accessibility Improvements
 *
 * Improves screen reader and keyboard support for Judge.me ratings,
 * review badges and histogram filters.
 *
 * Main improvements:
 * - removes unnecessary interactive roles from decorative stars;
 * - hides individual star icons from screen readers;
 * - provides one readable rating announcement;
 * - moves focus to the updated review list after histogram filtering.
 *
 * Every write below is skipped when the attribute already has the wanted
 * value. The fixes re-run from a page-wide MutationObserver, so a pass that
 * rewrote unchanged attributes used to trigger the next pass: on the home
 * page that was ~90,000 attribute writes during load. Now the first pass
 * that changes nothing is the last one.
 */

(function () {
  let updateScheduled = false;

  function setAttr(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function removeAttr(element, name) {
    if (element.hasAttribute(name)) element.removeAttribute(name);
  }

  /**
   * Moves focus into the updated review results after a Judge.me
   * histogram filter has been activated.
   */
  function moveFocusToUpdatedJudgeMeReviews() {
    const reviewsBody = document.querySelector(".jdgm-all-reviews__body");

    if (!reviewsBody) return;

    let focusMoved = false;

    function moveFocus() {
      if (focusMoved) return;

      const focusable = Array.from(
        reviewsBody.querySelectorAll(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(","),
        ),
      ).find(function (element) {
        return (
          window.ASA11y.isVisible(element) &&
          !element.closest(".jdgm-histogram")
        );
      });

      if (focusable) {
        focusMoved = true;
        focusable.focus();
        return;
      }

      /*
       * Fallback: if the review contains no interactive controls,
       * focus the first review itself programmatically.
       */
      const firstReview = reviewsBody.querySelector(".jdgm-rev");

      if (firstReview && window.ASA11y.isVisible(firstReview)) {
        focusMoved = true;
        firstReview.setAttribute("tabindex", "-1");
        firstReview.focus();
      }
    }

    const reviewsObserver = new MutationObserver(function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(moveFocus);
      });
    });

    reviewsObserver.observe(reviewsBody, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.setTimeout(moveFocus, 300);
    window.setTimeout(moveFocus, 700);

    window.setTimeout(function () {
      reviewsObserver.disconnect();
    }, 1500);
  }

  /**
   * Allows Judge.me histogram filters to update the reviews first,
   * then moves focus into the newly filtered review content.
   */
  function fixJudgeMeHistogramFocusA11y() {
    document
      .querySelectorAll(".jdgm-histogram__star")
      .forEach(function (histogramStar) {
        if (histogramStar.dataset.a11yFocusFixed === "true") return;

        histogramStar.addEventListener("keydown", function (event) {
          if (event.key !== "Enter" && event.key !== " ") return;

          window.setTimeout(moveFocusToUpdatedJudgeMeReviews, 0);
        });

        histogramStar.dataset.a11yFocusFixed = "true";
      });
  }

  /**
   * Removes duplicate or incorrect roles from Judge.me rating widgets
   * and provides a single useful accessible rating label.
   */
  function fixJudgeMeRatings() {
    /*
     * Badge and review ratings announce through a visually hidden label
     * next to them (fixJudgeMeRatingLabels); carousel ratings keep their
     * own aria-label.
     */
    document
      .querySelectorAll(".jdgm-carousel-item__review-rating")
      .forEach(function (rating) {
        const score = rating.dataset.score || rating.getAttribute("data-score");

        removeAttr(rating, "role");
        removeAttr(rating, "tabindex");
        removeAttr(rating, "aria-pressed");

        if (score) {
          setAttr(rating, "aria-label", score + " out of 5 stars");
        }
      });

    /*
     * Individual stars are decorative because the rating is announced
     * once by the parent rating element.
     */
    document.querySelectorAll(".jdgm-star").forEach(function (star) {
      removeAttr(star, "role");
      removeAttr(star, "tabindex");
      removeAttr(star, "aria-label");
      setAttr(star, "aria-hidden", "true");
    });

    document
      .querySelectorAll(".jdgm-all-reviews__summary-stars[tabindex]")
      .forEach(function (summaryStars) {
        summaryStars.removeAttribute("tabindex");
      });

    document.querySelectorAll(".jdgm-prev-badge").forEach(function (badge) {
      const text = badge
        .querySelector(".jdgm-prev-badge__text")
        ?.textContent?.trim();

      const score = badge.querySelector(".jdgm-prev-badge__stars")?.dataset
        .score;

      if (score && text) {
        setAttr(badge, "aria-label", score + " out of 5 stars, " + text);
      }
    });

    fixJudgeMeHistogramFocusA11y();
  }

  /**
   * Moves the rating announcement into visually hidden readable text,
   * preventing screen readers from announcing each star separately.
   * (Same end state as before: the rating is aria-hidden with no
   * aria-label, and a hidden "N out of 5 stars" label sits before it.)
   */
  function fixJudgeMeRatingLabels() {
    document
      .querySelectorAll(
        [".jdgm-prev-badge__stars", ".jdgm-rev__rating"].join(","),
      )
      .forEach(function (rating) {
        const score = rating.dataset.score || rating.getAttribute("data-score");
        const label = score
          ? score + " out of 5 stars"
          : rating.getAttribute("aria-label");

        removeAttr(rating, "tabindex");
        removeAttr(rating, "aria-pressed");

        if (!label) return;

        removeAttr(rating, "aria-label");
        removeAttr(rating, "role");
        setAttr(rating, "aria-hidden", "true");

        const parent = rating.parentElement;

        if (!parent) return;

        if (!parent.querySelector(".as-jdgm-rating-label")) {
          const screenReaderLabel = document.createElement("span");

          screenReaderLabel.className = "as-jdgm-rating-label m:hidden";
          screenReaderLabel.textContent = label;

          parent.insertBefore(screenReaderLabel, rating);
        }
      });
  }

  function runJudgeMeAccessibilityFixes() {
    fixJudgeMeRatings();
    fixJudgeMeRatingLabels();
  }

  function scheduleJudgeMeFixes() {
    if (updateScheduled) return;

    updateScheduled = true;

    const schedule = window.requestIdleCallback || window.requestAnimationFrame;

    schedule(function () {
      updateScheduled = false;
      runJudgeMeAccessibilityFixes();
    });
  }

  function initJudgeMeAccessibility() {
    runJudgeMeAccessibilityFixes();

    window.setTimeout(runJudgeMeAccessibilityFixes, 1000);
    window.setTimeout(runJudgeMeAccessibilityFixes, 2500);

    /*
     * Re-run when Judge.me renders or re-renders widgets (childList) or puts
     * an interactive attribute back. Class changes are not watched: sliders
     * and animations toggle classes constantly and none of them affect what
     * this script fixes.
     */
    new MutationObserver(scheduleJudgeMeFixes).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label", "role", "tabindex"],
    });
  }

  window.ASA11y.onReady(initJudgeMeAccessibility);
})();
